import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { currentUserSchema, sessionKey } from '@/features/auth';

import * as identityApi from '../api/identityApi';
import type {
  RegistrationLink,
  SetPasswordInput,
  TeamMembershipInput,
  TeamSummary,
  UserAccountState,
  UserRef,
  UsersPage,
} from '../model/types';

/** Zapytanie o stronę listy kont — wszystko, co ją zawęża, siedzi w kluczu. */
export interface UsersQuery {
  state: UserAccountState | undefined;
  /** ⭐ Zawężenie kolejki „czekają na wpuszczenie": konto bez hasła wchodzi LINKIEM. */
  hasPassword?: boolean | undefined;
  q: string;
  offset: number;
  limit: number;
}

export const identityKeys = {
  all: ['identity'] as const,
  users: (query: UsersQuery) =>
    [
      'identity',
      'users',
      query.state ?? 'all',
      query.hasPassword ?? 'any',
      query.q,
      query.offset,
      query.limit,
    ] as const,
  teams: () => ['identity', 'teams'] as const,
  members: (teamId: number) => ['identity', 'teams', teamId, 'members'] as const,
  registrationLink: () => ['identity', 'registration-link'] as const,
  org: (q: string) => ['identity', 'org', q] as const,
};

/**
 * Katalog organizacji jako **opcje zapytania**, a nie hook.
 *
 * ⭐ Omnibox rozwiązuje kilka nazw naraz i robi to własnym `useQueries` — tak samo jak przy
 * elementach. Gdyby dostał tu hooka, musiałby albo wołać go w pętli (czego React zabrania),
 * albo `identity` musiałoby przejąć orkiestrację cudzego ekranu. Opcje zapytania rozdzielają
 * to czysto: **`identity` jest właścicielem trasy i klucza, wołający — kolejności i liczby
 * zapytań.**
 *
 * `staleTime` jest długi, bo skład organizacji zmienia się rzadko, a nazwa raz rozwiązana
 * przy pisaniu ma nie kosztować przelotu przy każdej kolejnej literze.
 */
export function orgQueryOptions(q: string) {
  return {
    queryKey: identityKeys.org(q),
    queryFn: ({ signal }: { signal: AbortSignal }) => identityApi.fetchOrg(q, signal),
    staleTime: 30_000,
  };
}

/**
 * Unieważnienie po zmianie w organizacji.
 *
 * Prefiksem, bo zmiany krzyżują listy: dopisanie kogoś do zespołu rusza **licznik członków**
 * na liście zespołów, a dezaktywacja użytkownika zmienia listę filtrowaną po `active`,
 * której klucz zawiera wartość filtra.
 */
function invalidateOrg(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: identityKeys.all });
  // Własne zespoły siedzą w odpowiedzi o sesji, więc po zmianie członkostwa
  // powłoka pokazywałaby nieaktualny podpis pod nazwiskiem.
  void queryClient.invalidateQueries({ queryKey: ['auth'] });
}

/**
 * Strona listy kont.
 *
 * ⭐ `placeholderData` trzyma poprzednią stronę na ekranie, dopóki nie przyjdzie następna —
 * bez tego każde naciśnięcie klawisza w szukajce i każde przewrócenie strony **wygaszałoby
 * tabelę do szkieletu**, czyli skakałoby układem pod kursorem. Szkielet należy się pierwszemu
 * wejściu, nie kolejnemu zapytaniu o to samo.
 */
export function useUsers(query: UsersQuery) {
  return useQuery<UsersPage>({
    queryKey: identityKeys.users(query),
    queryFn: ({ signal }) =>
      identityApi.fetchUsers(
        {
          state: query.state,
          hasPassword: query.hasPassword,
          q: query.q,
          offset: query.offset,
          limit: query.limit,
        },
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useTeams() {
  return useQuery<TeamSummary[]>({
    queryKey: identityKeys.teams(),
    queryFn: ({ signal }) => identityApi.fetchTeams(signal),
  });
}

/**
 * Skład zespołu. `enabled` bierze się stąd, że panel bywa zamknięty — a wtedy nie ma po co
 * pytać. Klucz zawiera identyfikator zespołu, więc przełączenie zespołu jest nowym pytaniem,
 * a nie nadpisaniem poprzedniej odpowiedzi.
 */
export function useTeamMembers(teamId: number | null) {
  return useQuery<UserRef[]>({
    queryKey: identityKeys.members(teamId ?? 0),
    queryFn: ({ signal }) => identityApi.fetchTeamMembers(teamId!, signal),
    enabled: teamId !== null,
  });
}

export function useRegistrationLink() {
  return useQuery<RegistrationLink>({
    queryKey: identityKeys.registrationLink(),
    queryFn: ({ signal }) => identityApi.fetchRegistrationLink(signal),
  });
}

/**
 * Zmiana stanu konta: wpuszczenie, zablokowanie, odblokowanie.
 *
 * ⛔ **Bez optimistic UI, mimo że zmiana wygląda na lokalną.** Wpuszczenie jest w tym
 * produkcie **weryfikacją tożsamości** (spec sek. 10) — pokazanie „aktywny", zanim serwer
 * to potwierdzi, znaczyłoby „wpuściłem tę osobę" w chwili, w której jeszcze nie wiadomo,
 * czy się udało. Blokada idzie tą samą drogą, bo serwer odmawia jej ostatniemu żywemu
 * adminowi, a tego front nie umie przewidzieć.
 */
export function useChangeUserState() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: number; action: identityApi.UserStateAction }>({
    mutationFn: ({ id, action }) => identityApi.changeUserState(id, action),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

export function useSetUserPassword() {
  return useMutation<void, Error, SetPasswordInput>({
    mutationFn: ({ userId, newPassword }) => identityApi.setUserPassword(userId, newPassword),
  });
}


/**
 * Zmiana loginu (adresu e-mail).
 *
 * ⚠ Unieważnia sesje tego konta po stronie serwera — człowiek loguje się od tej chwili
 * czym innym. ⛔ Bez optimistic UI: kolizję loginu rozstrzyga wyłącznie serwer.
 */
export function useChangeUserLogin() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { userId: number; login: string }>({
    mutationFn: ({ userId, login }) => identityApi.changeUserLogin(userId, login),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation<TeamSummary, Error, { name: string; key: string }>({
    mutationFn: ({ name, key }) => identityApi.createTeam(name, key),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, TeamMembershipInput>({
    // Argumenty nazwane, tak jak po stronie API: `teamId` i `userId` to oba `number`,
    // więc zamiana miejscami skompilowałaby się i zapisała złe członkostwo.
    mutationFn: ({ teamId, userId }) => identityApi.addTeamMember(teamId, userId),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, TeamMembershipInput>({
    mutationFn: ({ teamId, userId }) => identityApi.removeTeamMember(teamId, userId),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation<TeamSummary, Error, { teamId: number; name: string; key: string }>({
    mutationFn: ({ teamId, name, key }) => identityApi.updateTeam(teamId, name, key),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

/**
 * Usunięcie zespołu. ⛔ Serwer odmawia, gdy zespół jest właścicielem elementów — front
 * tego nie sprawdza, bo własność liczy Graph, a Identity pyta go portem.
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (teamId) => identityApi.deleteTeam(teamId),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

/** Dopisanie kilku osób jednym żądaniem — wsad niepodzielny po stronie serwera. */
export function useAddTeamMembers() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { teamId: number; userIds: readonly number[] }>({
    mutationFn: ({ teamId, userIds }) => identityApi.addTeamMembers(teamId, userIds),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

/**
 * Role konta. ⛔ Odmowa przychodzi z serwera, gdy admin odbiera rolę sobie albo ostatniemu
 * aktywnemu adminowi — front tego nie powiela, bo „ostatni admin" to pytanie do bazy.
 */
export function useSetUserRoles() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { userId: number; roles: readonly string[] }>({
    mutationFn: ({ userId, roles }) => identityApi.setUserRoles(userId, roles),
    onSuccess: () => invalidateOrg(queryClient),
  });
}

/**
 * Zmiana **własnej** nazwy wyświetlanej.
 *
 * Odpowiedź to pełne „kim jestem", więc wkładamy ją wprost do pamięci podręcznej sesji:
 * nazwa stoi w powłoce i przy właścicielach, a ponowne odpytanie dałoby mignięcie starą.
 */
export function useUpdateOwnDisplayName() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: (displayName) => identityApi.updateOwnDisplayName(displayName),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKey, currentUserSchema.parse(session));
      void queryClient.invalidateQueries({ queryKey: identityKeys.all });
    },
  });
}

/**
 * Odnowienie sekretnego linku. ⚠ **Nieodwracalne** — poprzedni przestaje działać, więc
 * każdy, kto go dostał i jeszcze nie założył konta, musi dostać nowy.
 */
export function useRotateRegistrationLink() {
  const queryClient = useQueryClient();

  return useMutation<RegistrationLink, Error, void>({
    mutationFn: () => identityApi.rotateRegistrationLink(),
    onSuccess: (link) => queryClient.setQueryData(identityKeys.registrationLink(), link),
  });
}
